import React, { useState, useEffect } from 'react';
import { locationData } from '@/lib/indianLocations';
import { Globe, Heart, MapPin, User, Users } from 'lucide-react';

interface OnboardingData {
  gender?: string;
  birthDate?: string;
  interests?: string;
  lookingFor?: string;
  languages?: string;
  state?: string;
  city?: string;
}

interface Props {
  data: OnboardingData;
  onChange: (data: OnboardingData) => void;
  readOnly?: boolean;
}

const GENDERS = ['Male', 'Female', 'Non-Binary', 'Other'];
const LOOKING_FOR_OPTIONS = [
  'New Friends',
  'Activity Partners',
  'Online Chat',
  'Networking',
  'Travel Buddies',
  'Gaming Partners',
];

const ProfileOnboarding: React.FC<Props> = ({ data, onChange, readOnly = false }) => {
  const [cities, setCities] = useState<string[]>(
    data.state ? locationData.find(s => s.state === data.state)?.cities || [] : []
  );

  useEffect(() => {
    if (data.state) {
      const stateInfo = locationData.find(s => s.state === data.state);
      setCities(stateInfo?.cities || []);
    } else {
      setCities([]);
    }
  }, [data.state]);

  const update = (key: keyof OnboardingData, value: any) => {
    onChange({ ...data, [key]: value });
  };

  const inputClass = "w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 text-sm";
  const selectClass = "w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 text-sm cursor-pointer";

  return (
    <div className="space-y-6">
      {/* ─── Basic Info ─── */}
      <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        <User className="h-4 w-4" />
        {'Basic Details'}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Gender */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">{'Gender'}</label>
          <select
            value={data.gender || ''}
            onChange={e => update('gender', e.target.value || undefined)}
            disabled={readOnly}
            className={selectClass}
          >
            <option value="">{'Select...'}</option>
            {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        
        {/* Date of Birth */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">{'Date of Birth'}</label>
          <input
            type="date"
            value={data.birthDate || ''}
            onChange={e => update('birthDate', e.target.value || undefined)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      {/* ─── Preferences & Interests ─── */}
      <div className="space-y-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          <Heart className="h-4 w-4" />
          {'Preferences & Interests'}
        </h3>
        
        {/* Looking For */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">{'Looking For'}</label>
          <select
            value={data.lookingFor || ''}
            onChange={e => update('lookingFor', e.target.value || undefined)}
            disabled={readOnly}
            className={selectClass}
          >
            <option value="">{'Select...'}</option>
            {LOOKING_FOR_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>

        {/* Interests */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-muted-foreground">{'Interests (Comma separated)'}</label>
          <input
            type="text"
            placeholder="e.g. Hiking, Reading, Board Games"
            value={data.interests || ''}
            onChange={e => update('interests', e.target.value || undefined)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>

        {/* Languages */}
        <div className="space-y-1.5">
          <label className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Globe className="h-3.5 w-3.5" />
            {'Languages Spoken (Comma separated)'}
          </label>
          <input
            type="text"
            placeholder="e.g. English, Hindi, Spanish"
            value={data.languages || ''}
            onChange={e => update('languages', e.target.value || undefined)}
            disabled={readOnly}
            className={inputClass}
          />
        </div>
      </div>

      {/* ─── State → City ─── */}
      <div className="space-y-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          <MapPin className="h-4 w-4" />
          {'Location'}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* State */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{'State'}</label>
            <select
              value={data.state || ''}
              onChange={e => {
                onChange({
                  ...data,
                  state: e.target.value || undefined,
                  city: undefined
                });
              }}
              disabled={readOnly}
              className={selectClass}
            >
              <option value="">{'Select...'}</option>
              {locationData.map(s => <option key={s.code} value={s.state}>{s.state}</option>)}
            </select>
          </div>
          {/* City */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">{'City'}</label>
            <select
              value={data.city || ''}
              onChange={e => update('city', e.target.value || undefined)}
              disabled={readOnly || !data.state}
              className={selectClass}
            >
              <option value="">{'Select...'}</option>
              {cities.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileOnboarding;
